import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Appointment } from "@/lib/types"
import { hmFromMinutes } from "@/lib/time";
import { Badge } from "@/components/ui/badge"
import { useAppointmentModal } from "@/contexts/AppointmentModalContext"
import { useLinkedAppointments } from "@/contexts/LinkedAppointmentsContext"
import { useAccessPolicy } from "@/hooks/access/useAccess"
import { useIsMobile } from "@/hooks/use-mobile"
import { StatusGlyph } from "@/components/custom/appointment-modal/status-glyph"
import { Link, Trash2, MessageCircle } from "lucide-react"
import { useEditAppointmentRecord } from "@/hooks/modal/useEditAppointmentRecord"
import { AppointmentHoverCard } from "@/components/appointment-hover-card"
import { cardBody } from "@/lib/calendar/cardBody"

// Letter badge next to the clock (README §10.7): which messenger carried the
// pre-visit confirmation request. T = Telegram, W = WhatsApp, V = Viber.
// M and VK: the salon's own MAX / VK bot (Boona Direct, inbox plan INB6)
const ASK_CHANNEL_LETTER: Record<string, string> = { telegram: "T", whatsapp: "W", viber: "V", max: "M", vk: "VK", sms: "S" };
const ASK_CHANNEL_NAME: Record<string, string> = { telegram: "Telegram", whatsapp: "WhatsApp", viber: "Viber", max: "MAX", vk: "VK", sms: "SMS" };

interface AppointmentCardProps {
  appointment: Appointment
  /** sizing is handled by the absolute wrapper now */
  topOffset?: number
  heightPx?: number
}

export function AppointmentCard({ appointment }: AppointmentCardProps) {
  const { t } = useTranslation();
  const { openModal } = useAppointmentModal();
  const policy = useAccessPolicy();
  const isMobile = useIsMobile();
  const canRead = policy.records.read;
  const canShowPhone = policy.timetable.phonesVisible;

  // Linked appointments trembling
  const { tremblingIds, startTrembling, stopTrembling, skipNextDismiss } = useLinkedAppointments();
  const hasLinkedRecords = (appointment.linked_record_ids?.length ?? 0) > 0;
  const isTrembling = tremblingIds.has(String(appointment.id));

  // State for mobile staff popup control
  const [hoverOpen, setHoverOpen] = useState(false);

  // All normalization is now handled in the registry; use fields directly
  const title = appointment.title;
  const details = appointment.details;
  const clientName = appointment.client_name || appointment.client;
  const phone = appointment.phone;
  const maskedPhone = phone ? "••••••" : "";
  // a booking with no services still shows its comment (reserved time: «обучение»)
  const body = cardBody({ title, details, clientName, phone }, canShowPhone);

  /**
   * Click handler:
   * - Mobile + staff (no canRead): toggle hover popup
   * - Mobile + admin (canRead): open full modal
   * - Desktop: open full modal (if canRead)
   */
  const handleClick = () => {
    if (isMobile && !canRead) {
      // Staff on mobile: toggle the compact popup
      setHoverOpen((prev) => !prev);
    } else if (canRead) {
      // Anyone with read access: open full modal
      openModal(Number(appointment.id));
    }
  };

  return (
    <div
      data-status={appointment.status ?? undefined}
      className={`appointment-card relative w-full h-full flex flex-col rounded-md overflow-hidden cursor-pointer bg-[var(--color-appointment-body)]${isTrembling ? " appointment-card--tremble" : ""}`}
      onClick={handleClick}
      title={canRead ? undefined : t("calendar.insufficient_permissions", "Insufficient permissions to view record")}
    >
      <div className="appointment-card__header flex justify-between items-start p-0 rounded-t">
        <div className="flex items-center gap-1 pl-1 pt-1">
          <span className="[font-size:var(--booking-subfont)]">
            {/* Prefer normalized minutes for display; this guarantees salon-tz times. */}
            {/* We keep a legacy string fallback temporarily to avoid breaking unknown code paths. */}
            {typeof appointment.startMinOfDay === "number" && typeof appointment.durationMin === "number"
              ? `${hmFromMinutes(appointment.startMinOfDay)}—${hmFromMinutes(appointment.startMinOfDay + appointment.durationMin)}`
              : `${appointment.startTime}—${appointment.endTime}`}
          </span>
          {hasLinkedRecords && (
            <button
              type="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                skipNextDismiss(); // Prevent global handler from auto-dismissing
                // Toggle: if already trembling, stop; otherwise start
                if (isTrembling) {
                  stopTrembling();
                } else {
                  startTrembling([String(appointment.id), ...(appointment.linked_record_ids ?? [])]);
                }
              }}
              className="p-0.5 hover:bg-white/20 rounded transition-colors"
              aria-label={t("calendar.linked_appointments", "Linked appointments")}
              title={t("calendar.linked_appointments", "Linked appointments")}
            >
              <Link className="h-3 w-3 [color:var(--color-foreground-on-accent)]" />
            </button>
          )}
        </div>

        <AppointmentHoverCard
          appointment={appointment}
          open={isMobile && !canRead ? hoverOpen : undefined}
          onOpenChange={isMobile && !canRead ? setHoverOpen : undefined}
        >
          <div className="flex items-center gap-1 pr-1 pt-1 pb-1">
            {appointment.status === "waiting" && appointment.confirm_method != null && appointment.confirm_method !== 1 && (
              <span title="Messenger">
                <MessageCircle className="h-3 w-3 [color:var(--color-foreground-on-accent)]" />
              </span>
            )}
            {appointment.status === "waiting" && appointment.confirm_ask_channel && (
              // README §10.7: unconfirmed, but the client HAS been asked on a messenger —
              // the admin can skip the noon phone call for this one
              <span
                title={t("appointment.card.asked_via", "Confirmation request sent via {{channel}}", {
                  channel: ASK_CHANNEL_NAME[appointment.confirm_ask_channel] ?? appointment.confirm_ask_channel,
                })}
                className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border text-[9px] font-bold leading-none [border-color:var(--color-foreground-on-accent)] [color:var(--color-foreground-on-accent)]"
              >
                {ASK_CHANNEL_LETTER[appointment.confirm_ask_channel] ?? "?"}
              </span>
            )}
            {appointment.status ? (
              <StatusGlyph
                status={appointment.status}
                className="[color:var(--color-foreground-on-accent)]"
                title={appointment.status}
              />
            ) : null}
          </div>
        </AppointmentHoverCard>
      </div>

      {body.show && (
        <div className="appointment-card__body flex-1 px-2 py-[var(--booking-py)] min-h-0">
          {/* Services render across multiple lines using newline delimiters. */}
          {body.title && (
            <div className="flex flex-col gap-[2px]">
              <div className="[font-size:var(--booking-font)] font-medium leading-tight">
                {String(title).split("\n").map((line, idx) => (
                  <div key={idx}>{line}</div>
                ))}
              </div>
            </div>
          )}

          {details && (
            <div className="[font-size:var(--booking-subfont)] mt-0.5">
              {details}
            </div>
          )}

          {clientName && (
            <div className="[font-size:var(--booking-subfont)] mt-1">
              {(appointment as any).isNew && (
                <Badge variant="secondary" className="bg-gradient-to-r from-pink-400 to-pink-500 text-white text-[10px] px-1 rounded mr-1 h-4 shadow-sm">
                  new
                </Badge>
              )}
              {typeof clientName === "string"
                ? clientName
                : clientName && typeof clientName === "object" && "name" in clientName
                  ? (clientName as any).name
                  : null}
            </div>
          )}

          {canShowPhone ? (
            phone ? <div className="[font-size:var(--booking-subfont)]">{phone}</div> : null
          ) : body.maskedPhone ? (
            <div className="[font-size:var(--booking-subfont)] text-muted-foreground">
              {maskedPhone || "••••••"}
            </div>
          ) : null}
        </div>
      )}

    </div>
  )
}
